import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getLoggedinUser } from "../../../api";
import { getUserRole } from "../../../helpers/role_helper";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Spinner } from "reactstrap";
import { getSellerWithdrawal, patchSellerWithdrawal } from "../../../api/ecommerce";
import { toast, ToastContainer } from "react-toastify";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_ISSUE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "declined_payment", label: "Declined payment" },
  { value: "delivery_error", label: "Delivery / transfer error" },
  { value: "wrong_amount", label: "Wrong amount" },
  { value: "other", label: "Other" },
];

const WithDrawVendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = getUserRole(getLoggedinUser()) === "admin";
  const listHref = isAdmin ? "/vendor-withdraw" : "/seller-draw";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [row, setRow] = useState(null);

  const [status, setStatus] = useState("pending");
  const [paymentIssueType, setPaymentIssueType] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    document.title = "Seller Withdraw Request | TopCoinLand";
  }, []);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setRow(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSellerWithdrawal(id)
      .then((data) => {
        if (cancelled) return;
        setRow(data);
        setStatus(data.status || "pending");
        setPaymentIssueType(data.payment_issue_type || "");
        setTransactionReference(data.transaction_reference || "");
        setAdminNotes(data.admin_notes || "");
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err?.error || err?.message || "Failed to load withdrawal");
          setRow(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSave = async () => {
    if (!id || !isAdmin) return;
    setSaving(true);
    try {
      await patchSellerWithdrawal(id, {
        status,
        payment_issue_type: paymentIssueType || null,
        transaction_reference: transactionReference || null,
        admin_notes: adminNotes || null,
      });
      toast.success("Withdrawal updated");
      navigate(listHref, { replace: true });
    } catch (err) {
      toast.error(err?.error || err?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (r) => {
    if (!r) return "—";
    const c = r.currency || "GBP";
    const sym = c === "GBP" ? "£" : c === "EUR" ? "€" : `${c} `;
    return `${sym}${Number(r.amount || 0).toFixed(2)}`;
  };

  const sellerName =
    row?.company_name?.trim() ||
    `${row?.first_name || ""} ${row?.last_name || ""}`.trim() ||
    row?.seller_email ||
    "—";

  if (!id) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Withdraw Request" pageTitle="Seller" />
          <Card>
            <CardBody className="text-center py-5">
              <p className="text-muted">Select a withdrawal from the list.</p>
              <Link to={listHref} className="btn btn-primary">
                Back to withdrawals
              </Link>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Withdraw Request" pageTitle="Seller" />
          <div className="text-center py-5">
            <Spinner color="primary" /> Loading...
          </div>
        </Container>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Withdraw Request" pageTitle="Seller" />
          <Card>
            <CardBody className="text-center py-5">
              <p className="text-danger">Request not found.</p>
              <Button color="secondary" tag={Link} to={listHref}>
                Back to list
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Withdraw Request" pageTitle="Seller" />

        <Row className="mb-3">
          <Col>
            <Button color="outline-secondary" tag={Link} to={listHref} className="mb-2">
              <i className="ri-arrow-left-line me-1"></i> Back to withdrawals
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Withdrawal #{row.id}</h5>
                <Row className="mb-3">
                  <Col md={6}>
                    <p className="mb-1 text-muted small">Seller</p>
                    <p className="fw-medium mb-0">{sellerName}</p>
                    <p className="text-muted small mb-0">{row.seller_email}</p>
                  </Col>
                  <Col md={3}>
                    <p className="mb-1 text-muted small">Amount requested</p>
                    <p className="fw-semibold fs-5 mb-0">{formatAmount(row)}</p>
                  </Col>
                  <Col md={3}>
                    <p className="mb-1 text-muted small">Submitted</p>
                    <p className="mb-0">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                    </p>
                  </Col>
                </Row>

                {row.seller_note && (
                  <div className="border rounded p-3 bg-light-subtle mb-3">
                    <Label className="text-muted small">Seller note</Label>
                    <p className="mb-0">{row.seller_note}</p>
                  </div>
                )}

                <Card className="border bg-light mt-2 mb-4">
                  <CardBody>
                    <p className="fw-medium mb-3">Payout bank details (on file)</p>
                    <div className="d-flex flex-wrap justify-content-between gap-3">
                      <div>
                        <label className="text-dark text-opacity-50 text-uppercase small d-block">
                          Bank name
                        </label>
                        <p className="mb-0">{row.bank_name || "—"}</p>
                      </div>
                      <div>
                        <label className="text-dark text-opacity-50 text-uppercase small d-block">
                          Branch
                        </label>
                        <p className="mb-0">{row.branch || "—"}</p>
                      </div>
                      <div>
                        <label className="text-dark text-opacity-50 text-uppercase small d-block">
                          Account holder
                        </label>
                        <p className="mb-0">{row.account_holder_name || "—"}</p>
                      </div>
                      <div>
                        <label className="text-dark text-opacity-50 text-uppercase small d-block">
                          Account number
                        </label>
                        <p className="mb-0">{row.account_number || "—"}</p>
                      </div>
                      <div>
                        <label className="text-dark text-opacity-50 text-uppercase small d-block">
                          IFSC / Sort code
                        </label>
                        <p className="mb-0">{row.ifsc_code || "—"}</p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {isAdmin ? (
                  <>
                    <h6 className="mb-3">Admin processing</h6>
                    <Row>
                      <Col md={6}>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          className="mb-3"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label>Payment issue type (optional)</Label>
                        <Input
                          type="select"
                          className="mb-3"
                          value={paymentIssueType}
                          onChange={(e) => setPaymentIssueType(e.target.value)}
                        >
                          {PAYMENT_ISSUE_OPTIONS.map((o) => (
                            <option key={o.value || "empty"} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={12}>
                        <Label>Transaction / reference ID</Label>
                        <Input
                          type="text"
                          className="mb-3"
                          placeholder="e.g. bank transfer reference"
                          value={transactionReference}
                          onChange={(e) => setTransactionReference(e.target.value)}
                        />
                      </Col>
                      <Col md={12}>
                        <Label>Admin notes</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          className="mb-3"
                          placeholder="Internal notes"
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                        />
                      </Col>
                    </Row>

                    <Button color="primary" onClick={handleSave} disabled={saving}>
                      {saving ? "Saving..." : "Save changes"}
                    </Button>
                  </>
                ) : (
                  <div className="border rounded p-3 bg-light-subtle">
                    <Label className="text-muted small">Status</Label>
                    <p className="fw-medium mb-0 text-capitalize">{row.status || "pending"}</p>
                    {row.transaction_reference && (
                      <p className="small text-muted mb-0 mt-2">
                        Reference: {row.transaction_reference}
                      </p>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
};

export default WithDrawVendorDetails;
